import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { copyRecheckItemsToClipboard } from './maintenance-actions'
import { useMaintenanceAssessments } from './use-maintenance-assessments'
import { useMaintenanceChangeSubmission } from './use-maintenance-change-submission'
import { useMaintenanceImpact } from './use-maintenance-impact'
import { useMaintenanceResources } from './use-maintenance-resources'
import { useMaintenanceStep } from './use-maintenance-step'

/** 유지하기 화면에 필요한 평가, 이력, 영향 분석, 입력 액션 상태를 조립한다. */
export function useMaintenanceSession() {
    const { assessments, selectedId, setSelectedId, listError } =
        useMaintenanceAssessments()
    const stepControls = useMaintenanceStep({ selectedId, setSelectedId })
    const {
        history,
        checklist,
        answers,
        scores,
        selectedEventId,
        setSelectedEventId,
        selectedImpactId,
        setSelectedImpactId,
        loadError,
        resetActiveSelection,
        addEventToHistory,
        updateEventInHistory,
    } = useMaintenanceResources({
        selectedId,
        enabled: stepControls.step === 'analyze-change',
    })
    const selectedAssessment = assessments?.find(
        (item) => item.id === selectedId
    )
    const activeEvent =
        history?.find((item) => item.id === selectedEventId) ?? null
    const submission = useMaintenanceChangeSubmission({
        selectedId,
        enabled: stepControls.step === 'analyze-change',
        activeEvent,
        resetActiveSelection,
        addEventToHistory,
        updateEventInHistory,
    })
    const {
        resultItems,
        impactRows,
        recheckCount,
        keepCount,
        unaffectedCount,
        selectedImpact,
    } = useMaintenanceImpact({
        activeEvent,
        checklist,
        answers,
        scores,
        selectedImpactId,
    })
    const copyRecheckItems = () => {
        copyRecheckItemsToClipboard(resultItems)
    }

    // 인증 보고서와 동일한 "실제 라우트 새 창" 방식. 데이터는 localStorage 핸드오프로
    // 넘긴다(blob/popup 차단 회피, 클릭 제스처 동기 유지).
    const openImpactReport = (opts?: { print?: boolean }) => {
        localStorage.setItem(
            'haccp-maint-report',
            JSON.stringify({
                event: activeEvent,
                rows: impactRows,
                print: !!opts?.print,
            })
        )
        window.open('/maintenance/report', '_blank')
    }

    const startAnalysisStep = (nextSelectedId = selectedId) => {
        stepControls.startAnalysisStep(nextSelectedId, {
            resetActiveSelection,
            focusTextarea: submission.focusTextarea,
        })
    }

    const returnToAssessmentStep = () => {
        stepControls.returnToAssessmentStep({
            resetActiveSelection,
            clearSubmissionFeedback: submission.clearSubmissionFeedback,
        })
    }

    // 딥링크(`/maintenance?assessment=<id>`) — 인증 상세에서 넘어오면 그 평가를 프리셀렉트하고
    // 바로 2단계(변경 입력)로 진입한다. URL은 한 번 쓰고 지워 새로고침·뒤로가기 재실행을 막는다.
    const [searchParams, setSearchParams] = useSearchParams()
    const deepLinkConsumed = useRef(false)
    useEffect(() => {
        if (deepLinkConsumed.current || !assessments) return
        const targetId = searchParams.get('assessment')
        if (!targetId) return
        deepLinkConsumed.current = true
        searchParams.delete('assessment')
        setSearchParams(searchParams, { replace: true })
        if (assessments.some((item) => item.id === targetId)) {
            startAnalysisStep(targetId)
        }
        // startAnalysisStep는 매 렌더 새로 생성되지만, ref 가드로 1회만 실행된다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assessments])

    return {
        assessments,
        step: stepControls.step,
        selectedId,
        setSelectedId,
        description: submission.description,
        setDescription: submission.setDescription,
        submittedText: submission.submittedText,
        history,
        checklist,
        selectedEventId,
        setSelectedEventId,
        setSelectedImpactId,
        pendingClarification: submission.pendingClarification,
        progress: submission.progress,
        listError,
        actionError: submission.actionError ?? loadError,
        submitting: submission.submitting,
        textareaRef: submission.textareaRef,
        selectedAssessment,
        activeEvent,
        resultItems,
        recheckCount,
        keepCount,
        unaffectedCount,
        selectedImpact,
        handleSubmit: submission.handleSubmit,
        handleDescriptionKeyDown: submission.handleDescriptionKeyDown,
        submitClarificationAnswers: submission.submitClarificationAnswers,
        startAnalysisStep,
        returnToAssessmentStep,
        focusNewChange: submission.focusNewChange,
        copyRecheckItems,
        openImpactReport,
    }
}
